/**
 * Task detail (slice 2b spec §10.2; Paper F4 without the computer rail, board
 * 37): the title (renamable), status, agent, who it's for, when it started,
 * what done looks like, the actions the stage machine allows, what it has
 * cost (the owner's), and "What's happened" from its events. The `task` hub
 * event refetches it. On a phone it's one column, the actions in a sheet.
 */
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@crew/ui/components/alert-dialog";
import { Button, buttonVariants } from "@crew/ui/components/button";
import { AgentAvatar, AgentChip, PersonAvatar, PersonChip } from "@crew/ui/components/crew-avatar";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@crew/ui/components/dialog";
import { BackLink } from "@crew/ui/components/back-link";
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerHeader,
  DrawerTitle,
} from "@crew/ui/components/drawer";
import { Field, FieldDescription, FieldLabel } from "@crew/ui/components/field";
import { Input } from "@crew/ui/components/input";
import { Link } from "@crew/ui/components/link";
import { Spinner } from "@crew/ui/components/spinner";
import { Textarea } from "@crew/ui/components/textarea";
import { Timeline, TimelineEntry } from "@crew/ui/components/timeline";
import { PageTitle } from "@crew/ui/components/typography";
import { useIsMobile } from "@crew/ui/hooks/use-mobile";
import { cn } from "@crew/ui/lib/utils";
import { Check, Ellipsis, MessageSquare, Pencil, Repeat, RotateCcw, X } from "lucide-react";
import { useState, type FormEvent, type ReactNode } from "react";
import { useLocation } from "react-router-dom";
import { ApiError, errorHint } from "../../app/api";
import type { Me } from "../../app/me";
import { agentTint, initialsOf, personTint } from "../../app/tints";
import { shortTime } from "../chats/format";
import { panelHref } from "../chats/panel-state";
import { TaskDocs } from "../docs/task-docs";
import { useEditTask, useTask, type TaskDetail, type TaskEdit, type TaskEvent } from "./api";
import { todayIn } from "../areas/brief";
import { TaskStatus } from "./parts";
import { AreaPicker } from "../areas/area-controls";
import { eventLine, receiptsLine, taskActions } from "./task-view";

type Detail = TaskDetail["task"];
type Edit = ReturnType<typeof useEditTask>;

// ── The header ──────────────────────────────────────────────────────────────

function TitleEditor({ task, edit, onDone }: { task: Detail; edit: Edit; onDone: () => void }) {
  const [draft, setDraft] = useState(task.title);
  const save = (event: FormEvent) => {
    event.preventDefault();
    const title = draft.trim();
    if (!title || title === task.title) return onDone();
    edit.mutate({ title }, { onSuccess: onDone });
  };
  return (
    <form onSubmit={save} className="flex flex-col gap-1.5">
      <div className="flex items-center gap-2">
        <Input
          aria-label="Task title"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => e.key === "Escape" && onDone()}
          maxLength={140}
          autoFocus
          className="h-10 flex-1 text-base"
        />
        <Button type="submit" disabled={edit.isPending}>
          Save
        </Button>
        <Button type="button" variant="ghost" onClick={onDone}>
          Cancel
        </Button>
      </div>
    </form>
  );
}

function Title({ task, edit }: { task: Detail; edit: Edit }) {
  const [editing, setEditing] = useState(false);
  if (editing) return <TitleEditor task={task} edit={edit} onDone={() => setEditing(false)} />;
  return (
    <div className="group flex items-start gap-2">
      <PageTitle className="min-w-0 break-words" data-task-title>
        {task.title}
      </PageTitle>
      <Button
        variant="ghost"
        size="icon-sm"
        aria-label="Rename"
        className="mt-1.5 shrink-0 text-muted-foreground"
        onClick={() => setEditing(true)}
      >
        <Pencil />
      </Button>
    </div>
  );
}

/** Status, agent, who it's for, when it started, and its Area (slice 6d spec §4.2). */
function Facts({ task, me, edit }: { task: Detail; me: Me; edit: Edit }) {
  const forWho = task.for;
  return (
    <div className="flex flex-wrap items-center gap-x-5 gap-y-2">
      <TaskStatus
        task={task}
        viewerId={me.user.id}
        today={todayIn(me.workspace.timezone)}
        emphasis
      />
      <AgentChip
        name={task.agent.name}
        glyph={task.agent.glyph}
        tint={agentTint(task.agent.tint)}
      />
      {forWho ? (
        <PersonChip
          prefix="For"
          name={forWho.id === me.user.id ? "You" : forWho.name}
          initials={forWho.initials}
          tint={personTint(forWho.tint)}
        />
      ) : null}
      <span className="text-meta text-muted-foreground">
        Started {shortTime(task.created_at, me.workspace.timezone)}
      </span>
      <span data-task-area>
        <AreaPicker
          me={me}
          current={task.area ?? null}
          title="Which area is this task in?"
          line="Just this task. Its chat stays where it is."
          onPick={(areaId) => edit.mutate({ area_id: areaId })}
          busy={edit.isPending && edit.variables?.area_id !== undefined}
          className="-ml-2"
        />
      </span>
    </div>
  );
}

// ── The actions ─────────────────────────────────────────────────────────────

/** Why a change didn't happen, e.g. the agent moved the task first (`409 bad_transition`). */
function EditError({ edit }: { edit: Edit }) {
  if (!edit.error) return null;
  return (
    <p role="alert" className="text-meta text-brick">
      {errorHint(edit.error)}
    </p>
  );
}

/** Mark done, with an optional outcome: what was delivered. */
function MarkDoneWindow({
  open,
  onOpenChange,
  edit,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  edit: Edit;
}) {
  const [outcome, setOutcome] = useState("");
  const submit = (event: FormEvent) => {
    event.preventDefault();
    const note = outcome.trim();
    edit.mutate(
      { stage: "done", ...(note ? { note } : {}) },
      {
        onSuccess: () => {
          setOutcome("");
          onOpenChange(false);
        },
      },
    );
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[480px]">
        <DialogHeader>
          <DialogTitle>Mark done</DialogTitle>
          <DialogDescription>Nothing more happens on it unless you reopen it.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="flex flex-col gap-4" data-mark-done>
          <Field>
            <FieldLabel htmlFor="task-outcome">How did it end?</FieldLabel>
            <Textarea
              id="task-outcome"
              value={outcome}
              onChange={(e) => setOutcome(e.target.value)}
              maxLength={1000}
              rows={3}
              placeholder="Booked the plumber for Thursday at 9."
            />
            <FieldDescription>Optional. It shows on the task.</FieldDescription>
          </Field>
          <EditError edit={edit} />
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
              Back
            </Button>
            <Button type="submit" disabled={edit.isPending}>
              Mark done
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/** Cancel's confirm: no turn, a line in the thread, reopenable. */
function CancelConfirm({
  open,
  onOpenChange,
  edit,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  edit: Edit;
}) {
  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent size="sm">
        <AlertDialogHeader>
          <AlertDialogTitle>Cancel this task?</AlertDialogTitle>
          <AlertDialogDescription>
            The crew stops working on it, and the chat says you canceled it. You can reopen it
            later.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <EditError edit={edit} />
        <AlertDialogFooter>
          <AlertDialogCancel>Keep it</AlertDialogCancel>
          <AlertDialogAction
            variant="destructive"
            disabled={edit.isPending}
            onClick={() =>
              edit.mutate({ stage: "canceled" }, { onSuccess: () => onOpenChange(false) })
            }
          >
            Cancel task
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

type Windows = { done: () => void; cancel: () => void };

type ActionItem = {
  key: string;
  label: string;
  icon: ReactNode;
  run: () => void;
  destructive?: boolean;
};

/** The actions the stage machine allows, in order (spec §10.2). */
function actionItems(task: Detail, change: (e: TaskEdit) => void, open: Windows): ActionItem[] {
  const allowed = taskActions(task.stage);
  const items: ActionItem[] = [];
  if (allowed.markDone) {
    items.push({ key: "done", label: "Mark done", icon: <Check />, run: open.done });
  }
  if (allowed.reopen) {
    items.push({
      key: "reopen",
      label: "Reopen",
      icon: <RotateCcw />,
      run: () => change({ stage: "queued" }),
    });
  }
  if (allowed.ongoing) {
    items.push({
      key: "ongoing",
      label: task.ongoing ? "Not ongoing" : "Mark ongoing",
      icon: <Repeat />,
      run: () => change({ ongoing: !task.ongoing }),
    });
  }
  if (allowed.cancel) {
    items.push({
      key: "cancel",
      label: "Cancel task",
      icon: <X />,
      run: open.cancel,
      destructive: true,
    });
  }
  return items;
}

function OpenChat({ task, className }: { task: Detail; className?: string }) {
  const { search } = useLocation();
  return (
    <Link
      href={panelHref(search, task.thread_id)}
      data-open-chat
      className={cn(buttonVariants({ variant: "default" }), className)}
    >
      <MessageSquare />
      Open the chat
    </Link>
  );
}

/** The phone's actions: a bottom sheet. */
function ActionSheet({ items }: { items: ActionItem[] }) {
  const [open, setOpen] = useState(false);
  if (items.length === 0) return null;
  return (
    <>
      <Button variant="outline" onClick={() => setOpen(true)} data-task-actions-button>
        <Ellipsis />
        Actions
      </Button>
      <Drawer open={open} onOpenChange={setOpen} showSwipeHandle>
        <DrawerContent showCloseButton className="pb-[max(1rem,env(safe-area-inset-bottom))]">
          <DrawerHeader>
            <DrawerTitle>This task</DrawerTitle>
            <DrawerDescription className="sr-only">
              What you can do with this task
            </DrawerDescription>
          </DrawerHeader>
          <div className="flex flex-col gap-1 px-2 pt-3" data-task-actions>
            {items.map((item) => (
              <Button
                key={item.key}
                variant="ghost"
                data-action={item.key}
                className={cn(
                  "h-12 justify-start gap-3 px-3 text-base",
                  item.destructive && "text-brick hover:text-brick",
                )}
                onClick={() => {
                  setOpen(false);
                  item.run();
                }}
              >
                {item.icon}
                {item.label}
              </Button>
            ))}
          </div>
        </DrawerContent>
      </Drawer>
    </>
  );
}

function Actions({ task, edit }: { task: Detail; edit: Edit }) {
  const mobile = useIsMobile();
  const [doneOpen, setDoneOpen] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  // Each action starts without the last one's error; a window shows its own (Devin on #260).
  const items = actionItems(task, (e) => edit.mutate(e), {
    done: () => {
      edit.reset();
      setDoneOpen(true);
    },
    cancel: () => {
      edit.reset();
      setCancelOpen(true);
    },
  });
  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-wrap items-center gap-2">
        <OpenChat task={task} className={mobile ? "flex-1" : undefined} />
        {mobile ? (
          <ActionSheet items={items} />
        ) : (
          <div className="flex flex-wrap items-center gap-2" data-task-actions>
            {items.map((item) => (
              <Button
                key={item.key}
                variant="outline"
                data-action={item.key}
                className={cn(item.destructive && "text-brick hover:text-brick")}
                onClick={item.run}
              >
                {item.icon}
                {item.label}
              </Button>
            ))}
          </div>
        )}
      </div>
      {doneOpen || cancelOpen ? null : <EditError edit={edit} />}
      <MarkDoneWindow open={doneOpen} onOpenChange={setDoneOpen} edit={edit} />
      <CancelConfirm open={cancelOpen} onOpenChange={setCancelOpen} edit={edit} />
    </div>
  );
}

// ── What's happened ─────────────────────────────────────────────────────────

/** The avatar beside an event: the person, the agent, or Crew itself. */
function ActorAvatar({ event, me }: { event: TaskEvent; me: Me }) {
  const { actor } = event;
  if (actor.kind === "agent") {
    const agent = me.agents.find((a) => a.id === actor.id);
    return (
      <AgentAvatar glyph={agent?.glyph ?? "✳"} tint={agentTint(agent?.tint ?? "ink")} size={22} />
    );
  }
  if (actor.kind === "person") {
    const mine = actor.id === me.user.id;
    const initials = mine ? me.user.initials : initialsOf(actor.name);
    return (
      <PersonAvatar initials={initials} tint={personTint(mine ? me.user.tint : "sand")} size={22} />
    );
  }
  return <AgentAvatar glyph="✳" tint="ink" size={22} />;
}

function EventBody({ event, owner }: { event: TaskEvent; owner: boolean }) {
  const { line, note } = eventLine(event);
  return (
    <div className="flex flex-col gap-1">
      {/* A note event's line is the note itself: keep its line breaks (Devin on #260). */}
      <p className="text-[14px] leading-[21px] whitespace-pre-wrap text-copy">{line}</p>
      {note ? (
        <p className="border-l-2 border-border pl-3 text-[14px] leading-[21px] whitespace-pre-wrap text-muted-foreground">
          {note}
        </p>
      ) : null}
      {owner && event.turn_id ? (
        <Link
          href={`/activity/${event.turn_id}`}
          data-receipt-link={event.turn_id}
          className="-my-2 w-fit py-2 text-xs text-muted-foreground underline-offset-2 hover:text-foreground hover:underline"
        >
          Receipt
        </Link>
      ) : null}
    </div>
  );
}

function WhatsHappened({ detail, me }: { detail: TaskDetail; me: Me }) {
  const owner = detail.receipts !== null;
  const events = detail.events;
  return (
    <section className="flex flex-col gap-3" data-timeline>
      <h2 className="font-display text-[22px] leading-7 text-foreground md:text-[26px] md:leading-8">
        What's happened
      </h2>
      <Timeline>
        {events.map((event, index) => (
          <TimelineEntry
            key={event.id}
            avatar={<ActorAvatar event={event} me={me} />}
            name={event.actor.id === me.user.id ? "You" : event.actor.name}
            time={shortTime(event.created_at, me.workspace.timezone)}
            last={index === events.length - 1}
          >
            <EventBody event={event} owner={owner} />
          </TimelineEntry>
        ))}
      </Timeline>
    </section>
  );
}

// ── The page ────────────────────────────────────────────────────────────────

function LoadedTask({ detail, me }: { detail: TaskDetail; me: Me }) {
  const { task, receipts } = detail;
  const edit = useEditTask(task.id);
  return (
    <main
      data-task-detail={task.id}
      className="flex w-full max-w-3xl min-w-0 flex-col gap-7 px-4 pt-6 pb-16 sm:px-6 md:px-10 md:pt-8"
    >
      <header className="flex flex-col gap-3">
        <BackLink href="/tasks">Tasks</BackLink>
        <Title task={task} edit={edit} />
        <Facts task={task} me={me} edit={edit} />
        {task.done_looks_like ? (
          <p data-task-goal className="max-w-[620px] text-body leading-6 text-copy">
            {task.done_looks_like}
          </p>
        ) : null}
        {receipts ? (
          <p data-task-receipts className="text-meta text-muted-foreground">
            {receiptsLine(receipts)}
          </p>
        ) : null}
      </header>
      <Actions task={task} edit={edit} />
      <TaskDocs taskId={task.id} docs={detail.docs ?? []} me={me} />
      <WhatsHappened detail={detail} me={me} />
    </main>
  );
}

export function TaskDetailPage({ taskId, me }: { taskId: string; me: Me }) {
  const task = useTask(taskId);
  if (task.data) return <LoadedTask detail={task.data} me={me} />;
  if (task.error) {
    const gone = task.error instanceof ApiError && task.error.status === 404;
    return (
      <main className="flex flex-col gap-3 px-4 pt-10 sm:px-6 md:px-10">
        <p className="text-body text-muted-foreground">
          {gone ? "This task isn't in your workspace." : errorHint(task.error)}
        </p>
        <Link href="/tasks" className="text-meta text-foreground underline underline-offset-2">
          All tasks
        </Link>
      </main>
    );
  }
  return (
    <div className="flex flex-1 items-center justify-center py-20 text-muted-foreground">
      <Spinner className="size-5" />
    </div>
  );
}
